import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LogOut, User, Heart, LayoutDashboard, Menu, X, ChevronDown, HelpCircle, Gift, Sparkles, Palette } from "lucide-react";
import { cn } from "@/lib/utils";
import speiseGuruLogoSvg from "@/assets/speiseguru_logo.svg";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const triggerRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const [dropdownPositions, setDropdownPositions] = useState<{ [key: string]: { top: number; left: number } }>({});
  
  const isAdmin = user?.roles?.includes("ROLE_ADMIN");

  // Close mobile menu + restore body scroll
  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = '';
  }, []);
  
  // Close mobile menu on route change
  useEffect(() => {
    closeMobileMenu();
  }, [location, closeMobileMenu]);

  const toggleMobileMenu = () => {
    const willOpen = !isMobileMenuOpen;
    setIsMobileMenuOpen(willOpen);
    // Lock body scroll when mobile menu is open
    if (willOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  // Cleanup scroll lock on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Schließe Dropdowns beim Klicken außerhalb
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      let clickedInside = false;
      
      Object.keys(dropdownRefs.current).forEach((key) => {
        const ref = dropdownRefs.current[key];
        if (ref && ref.contains(target)) {
          clickedInside = true;
        }
      });
      
      Object.keys(triggerRefs.current).forEach((key) => {
        const ref = triggerRefs.current[key];
        if (ref && ref.contains(target)) {
          clickedInside = true;
        }
      });

      if (!clickedInside && openDropdown) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return () => {};
  }, [openDropdown]);
  
  // Update Position bei Scroll/Resize
  useEffect(() => {
    const updatePositions = () => {
      if (openDropdown && triggerRefs.current[openDropdown]) {
        const trigger = triggerRefs.current[openDropdown];
        const rect = trigger.getBoundingClientRect();
        setDropdownPositions(prev => ({
          ...prev,
          [openDropdown]: {
            top: rect.bottom + 4,
            left: openDropdown === 'user' ? rect.right - 224 : rect.left
          }
        }));
      }
    };

    if (openDropdown) {
      updatePositions();
      window.addEventListener('scroll', updatePositions, { passive: true, capture: true });
      window.addEventListener('resize', updatePositions, { passive: true });
      return () => {
        window.removeEventListener('scroll', updatePositions, { capture: true } as EventListenerOptions);
        window.removeEventListener('resize', updatePositions);
      };
    }
    return () => {};
  }, [openDropdown]);

  const navItems = [
    { name: "Entdecken", path: "/", icon: "fas fa-search" },
    { name: "Restaurants", path: "/restaurants", icon: "fas fa-utensils" },
  ];

  const toggleDropdown = (dropdownName: string) => {
    const isOpening = openDropdown !== dropdownName;
    setOpenDropdown(isOpening ? dropdownName : null);
    
    if (isOpening && triggerRefs.current[dropdownName]) {
      const trigger = triggerRefs.current[dropdownName];
      const rect = trigger.getBoundingClientRect();
      setDropdownPositions(prev => ({
        ...prev,
        [dropdownName]: {
          top: rect.bottom + 4,
          left: dropdownName === 'user' ? rect.right - 224 : rect.left
        }
      }));
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-nav bg-black shadow-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop Layout */}
        <div className="hidden lg:flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link href="/" className="flex-shrink-0 flex items-center">
              <img
                src={speiseGuruLogoSvg}
                alt="SpeiseGuru Logo"
                className="h-8 w-auto"
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="flex items-center space-x-2 flex-1 justify-end">
            {/* Navigation Links */}
            {navItems.map((item) => (
              <Link
                key={item.path}
                href={item.path}
                className={cn(
                  "px-4 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors duration-200 relative",
                  location === item.path
                    ? "text-primary"
                    : "text-white hover:text-primary"
                )}
              >
                <i className={`${item.icon} text-xs`}></i>
                {item.name}
                {location === item.path && (
                  <div className="absolute bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full"></div>
                )}
              </Link>
            ))}
            
            {/* Für Restaurants Dropdown */}
            <div className="relative">
              <button
                ref={(el) => triggerRefs.current['restaurants'] = el}
                onClick={() => toggleDropdown('restaurants')}
                className={cn(
                  "px-4 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors duration-200 relative",
                  location.startsWith('/warum-speiseguru') || location.startsWith('/wir-gestalten')
                    ? "text-primary"
                    : "text-white hover:text-primary"
                )}
              >
                <i className="fas fa-store text-xs"></i>
                <span>Für Restaurants</span>
                <ChevronDown className={cn("ml-1 h-4 w-4 transition-transform duration-150", openDropdown === 'restaurants' ? 'rotate-180' : '')} />
                {(location.startsWith('/warum-speiseguru') || location.startsWith('/wir-gestalten')) && (
                  <div className="absolute bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full"></div>
                )}
              </button>
              {openDropdown === 'restaurants' && createPortal(
                <div 
                  className="fixed w-56 bg-black rounded-md shadow-2xl border border-white/10 z-dropdown" 
                  style={{ 
                    top: `${dropdownPositions['restaurants']?.top || 0}px`,
                    left: `${dropdownPositions['restaurants']?.left || 0}px`,
                  }}
                  ref={(el) => dropdownRefs.current['restaurants'] = el}
                >
                  <div className="py-1">
                    <div className="px-3 py-2 text-sm font-semibold text-white border-b border-white/10">
                      Für Restaurants
                    </div>
                    <Link
                      href="/warum-speiseguru"
                      onClick={() => setOpenDropdown(null)}
                      className={cn(
                        "block px-3 py-2 text-sm",
                        location === '/warum-speiseguru'
                          ? "text-primary bg-white/10"
                          : "text-white hover:text-primary hover:bg-white/10"
                      )}
                    >
                      <div className="flex items-center">
                        <Sparkles className="mr-2 h-4 w-4" />
                        <span>Warum SpeiseGURU?</span>
                      </div>
                    </Link>
                    <Link
                      href="/wir-gestalten"
                      onClick={() => setOpenDropdown(null)}
                      className={cn(
                        "block px-3 py-2 text-sm",
                        location === '/wir-gestalten'
                          ? "text-primary bg-white/10"
                          : "text-white hover:text-primary hover:bg-white/10"
                      )}
                    >
                      <div className="flex items-center">
                        <Palette className="mr-2 h-4 w-4" />
                        <span>Wir gestalten</span>
                      </div>
                    </Link>
                  </div>
                </div>,
                document.body
              )}
            </div>
            
            {/* Für Hungrige Dropdown */}
            <div className="relative">
              <button
                ref={(el) => triggerRefs.current['hungrige'] = el}
                onClick={() => toggleDropdown('hungrige')}
                className={cn(
                  "px-4 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors duration-200 relative",
                  location.startsWith('/wie-funktioniert') || location.startsWith('/gutschein')
                    ? "text-primary"
                    : "text-white hover:text-primary"
                )}
              >
                <i className="fas fa-user text-xs"></i>
                <span>Für Hungrige</span>
                <ChevronDown className={cn("ml-1 h-4 w-4 transition-transform duration-150", openDropdown === 'hungrige' ? 'rotate-180' : '')} />
                {(location.startsWith('/wie-funktioniert') || location.startsWith('/gutschein')) && (
                  <div className="absolute bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full"></div>
                )}
              </button>
              {openDropdown === 'hungrige' && createPortal(
                <div 
                  className="fixed w-56 bg-black rounded-md shadow-2xl border border-white/10 z-dropdown" 
                  style={{ 
                    top: `${dropdownPositions['hungrige']?.top || 0}px`,
                    left: `${dropdownPositions['hungrige']?.left || 0}px`,
                  }}
                  ref={(el) => dropdownRefs.current['hungrige'] = el}
                >
                  <div className="py-1">
                    <div className="px-3 py-2 text-sm font-semibold text-white border-b border-white/10">
                      Für Hungrige
                    </div>
                    <Link
                      href="/wie-funktioniert"
                      onClick={() => setOpenDropdown(null)}
                      className={cn(
                        "block px-3 py-2 text-sm",
                        location === '/wie-funktioniert'
                          ? "text-primary bg-white/10"
                          : "text-white hover:text-primary hover:bg-white/10"
                      )}
                    >
                      <div className="flex items-center">
                        <HelpCircle className="mr-2 h-4 w-4" />
                        <span>Wie funktioniert SpeiseGURU?</span>
                      </div>
                    </Link>
                    <Link
                      href="/gutschein-gewinnen"
                      onClick={() => setOpenDropdown(null)}
                      className={cn(
                        "block px-3 py-2 text-sm",
                        location === '/gutschein-gewinnen'
                          ? "text-primary bg-white/10"
                          : "text-white hover:text-primary hover:bg-white/10"
                      )}
                    >
                      <div className="flex items-center">
                        <Gift className="mr-2 h-4 w-4" />
                        <span>Gutschein gewinnen</span>
                      </div>
                    </Link>
                  </div>
                </div>,
                document.body
              )}
            </div>
            
            {/* Authentication Menu */}
            <div className="pl-3 border-l border-white/10">
              {user ? (
                <div className="relative">
                  <button
                    ref={(el) => triggerRefs.current['user'] = el}
                    onClick={() => toggleDropdown('user')}
                    className="flex items-center space-x-2"
                  >
                    <Avatar className="h-8 w-8 border border-primary">
                      {user.avatarUrl && (
                        <AvatarImage src={user.avatarUrl} alt={user.firstName || user.email} />
                      )}
                      <AvatarFallback className="bg-gradient-to-r from-primary to-primary-dark text-white">
                        {user.firstName ? user.firstName.substring(0, 1).toUpperCase() : user.email ? user.email.substring(0, 2).toUpperCase() : "U"}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden lg:inline-block text-sm font-medium text-white">
                      {user.firstName || (user.email ? user.email.split('@')[0] : "Benutzer")}
                    </span>
                    <ChevronDown className={cn("ml-1 h-4 w-4 transition-transform duration-150", openDropdown === 'user' ? 'rotate-180' : '')} />
                  </button>
                  {openDropdown === 'user' && createPortal(
                    <div 
                      className="fixed w-56 bg-black rounded-md shadow-2xl border border-white/10 z-dropdown" 
                      style={{ 
                        top: `${dropdownPositions['user']?.top || 0}px`,
                        left: `${dropdownPositions['user']?.left || 0}px`,
                      }}
                      ref={(el) => dropdownRefs.current['user'] = el}
                    >
                      <div className="py-1">
                        <div className="px-3 py-2 text-sm font-semibold text-white border-b border-white/10">
                          Mein Konto
                        </div>
                        <Link
                          href="/profil"
                          onClick={() => setOpenDropdown(null)}
                          className="block px-3 py-2 text-sm text-white hover:text-primary hover:bg-white/10"
                        >
                          <div className="flex items-center">
                            <User className="mr-2 h-4 w-4" />
                            <span>Mein Profil</span>
                          </div>
                        </Link>
                      <Link
                        href="/profil"
                        onClick={() => setOpenDropdown(null)}
                        className="block px-3 py-2 text-sm text-white hover:text-primary hover:bg-white/10"
                      >
                        <div className="flex items-center">
                          <Heart className="mr-2 h-4 w-4" />
                          <span>Meine Favoriten</span>
                        </div>
                      </Link>
                        {isAdmin && (
                          <Link
                            href="/admin"
                            onClick={() => setOpenDropdown(null)}
                            className="block px-3 py-2 text-sm text-white hover:text-primary hover:bg-white/10"
                          >
                            <div className="flex items-center">
                              <LayoutDashboard className="mr-2 h-4 w-4" />
                              <span>Admin-Dashboard</span>
                            </div>
                          </Link>
                        )}
                        <button
                          onClick={() => {
                            logoutMutation.mutate();
                            setOpenDropdown(null);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-white/10"
                        >
                          <div className="flex items-center">
                            <LogOut className="mr-2 h-4 w-4" />
                            <span>Abmelden</span>
                            {logoutMutation.isPending && (
                              <div className="ml-2 h-4 w-4 animate-spin rounded-full border-2 border-red-400 border-t-transparent" />
                            )}
                          </div>
                        </button>
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Link href="/auth">
                    <Button size="sm" className="bg-[#0E7F4F] hover:bg-[#0A6B42] text-white font-medium px-4 h-9 rounded-lg shadow-sm">
                      Anmelden
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden">
          <div className="flex items-center justify-between h-14">
            <Link href="/" className="flex items-center">
              <img
                src={speiseGuruLogoSvg}
                alt="SpeiseGuru Logo"
                className="h-8 w-auto"
              />
            </Link>
            <button
              onClick={toggleMobileMenu}
              className="text-white hover:text-primary p-2 rounded-lg hover:bg-white/10 transition-colors duration-200"
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {isMobileMenuOpen && createPortal(
            <div className="fixed inset-x-0 top-14 bottom-0 bg-black/95 backdrop-blur-md z-dropdown animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="h-full overflow-auto px-4 py-6 overscroll-contain">
                <div className="space-y-5">
                  {/* Navigation */}
                  <div>
                    <div className="text-white/60 text-xs font-medium uppercase tracking-wide mb-2">
                      Navigation
                    </div>
                    {navItems.map((item) => (
                      <Link
                        key={item.path}
                        href={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={cn(
                          "flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200",
                          location === item.path
                            ? "text-primary bg-white/10"
                            : "text-white hover:text-primary hover:bg-white/5"
                        )}
                      >
                        <i className={`${item.icon} text-lg w-6 text-center`}></i>
                        {item.name}
                      </Link>
                    ))}
                  </div>

                  {/* Für Restaurants */}
                  <div className="pt-3 border-t border-white/10">
                    <div className="text-white/60 text-xs font-medium uppercase tracking-wide mb-2">
                      Für Restaurants
                    </div>
                    <Link
                      href="/warum-speiseguru"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200",
                        location === '/warum-speiseguru' ? "text-primary bg-white/10" : "text-white hover:text-primary hover:bg-white/5"
                      )}
                    >
                      <Sparkles className="w-5 h-5" />
                      Warum SpeiseGURU?
                    </Link>
                    <Link
                      href="/wir-gestalten"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200",
                        location === '/wir-gestalten' ? "text-primary bg-white/10" : "text-white hover:text-primary hover:bg-white/5"
                      )}
                    >
                      <Palette className="w-5 h-5" />
                      Wir gestalten
                    </Link>
                  </div>

                  {/* Für Hungrige */}
                  <div className="pt-3 border-t border-white/10">
                    <div className="text-white/60 text-xs font-medium uppercase tracking-wide mb-2">
                      Für Hungrige
                    </div>
                    <Link
                      href="/wie-funktioniert"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200",
                        location === '/wie-funktioniert' ? "text-primary bg-white/10" : "text-white hover:text-primary hover:bg-white/5"
                      )}
                    >
                      <HelpCircle className="w-5 h-5" />
                      Wie funktioniert es?
                    </Link>
                    <Link
                      href="/gutschein-gewinnen"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200",
                        location === '/gutschein-gewinnen' ? "text-primary bg-white/10" : "text-white hover:text-primary hover:bg-white/5"
                      )}
                    >
                      <Gift className="w-5 h-5" />
                      Gutschein gewinnen
                    </Link>
                  </div>

                  {/* Account */}
                  {user ? (
                    <div className="pt-3 border-t border-white/10">
                      <div className="text-white/60 text-xs font-medium uppercase tracking-wide mb-2">
                        Mein Konto
                      </div>
                      <Link
                        href="/profil"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-white hover:text-primary hover:bg-white/5 rounded-xl transition-colors duration-200"
                      >
                        <User className="w-5 h-5" />
                        Mein Profil
                      </Link>
                      <Link
                        href="/profil"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-white hover:text-primary hover:bg-white/5 rounded-xl transition-colors duration-200"
                      >
                        <Heart className="w-5 h-5" />
                        Meine Favoriten
                      </Link>
                      {isAdmin && (
                        <Link
                          href="/admin"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 text-white hover:text-primary hover:bg-white/5 rounded-xl transition-colors duration-200"
                        >
                          <LayoutDashboard className="w-5 h-5" />
                          Admin-Dashboard
                        </Link>
                      )}
                      <button
                        onClick={() => {
                          logoutMutation.mutate();
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:text-red-300 hover:bg-white/5 rounded-xl transition-colors duration-200"
                      >
                        <LogOut className="w-5 h-5" />
                        Abmelden
                      </button>
                    </div>
                  ) : (
                    <div className="pt-3 border-t border-white/10">
                      <Link
                        href="/auth"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-[#0E7F4F] hover:bg-[#0A6B42] text-white rounded-xl font-semibold text-sm transition-colors duration-200"
                      >
                        <User className="w-4 h-4" />
                        Anmelden / Registrieren
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;